import { type FC, useEffect, useMemo } from 'react';

import { useTranslation } from '@crehana/i18n';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { useForm } from 'react-hook-form';

import useAttributes from '@/views/AdminDashboard/components/AttributesDrawer/hooks/useAttributes';
import TextFieldActions from '@/views/AdminDashboard/components/TextFieldActions';

import { MAX_LENGTH_ATTRIBUTE } from '../constants';

interface Attribute {
  originalId: string;
  name: string;
}

interface AttributeFormProps {
  attribute: Attribute;
  isDark?: boolean;
  onClose: () => void;
}

const AttributeForm: FC<React.PropsWithChildren<AttributeFormProps>> = ({
  attribute,
  isDark,
  onClose,
}) => {
  const { t } = useTranslation();
  const { attributes, editAttribute, loadingPUT } = useAttributes();

  const {
    handleSubmit,
    register,
    watch,
    setError,
    errors,
    clearErrors,
    formState: { isValid, isDirty },
  } = useForm<Attribute>({
    mode: 'onChange',
    defaultValues: useMemo(() => attribute, [attribute]),
  });

  useEffect(() => {
    register('originalId');
  }, [register]);

  const onSubmit = async ({ name, originalId }: Attribute) => {
    const isDuplicated = attributes?.filter(attr => attr.name === name).length;

    if (isDuplicated) {
      setError('name', {
        type: 'duplicated',
        message: t('ATTR_ATTR_DUPLICATED'),
      });
    } else {
      await editAttribute({ id: originalId, name: name });
      onClose();
    }
  };

  return (
    <form className="flex flex-row w-full" onSubmit={handleSubmit(onSubmit)}>
      <TextFieldActions
        isDark={isDark}
        name="name"
        hint={t('ATTR_INPUT_ADD')}
        maxLength={MAX_LENGTH_ATTRIBUTE}
        valueLength={watch('name')?.length || 0}
        submitDisabled={!isValid || !isDirty || loadingPUT}
        errorText={errors.name?.message}
        onCancel={onClose}
        onChange={e => e.target.value === '' && clearErrors()}
        ref={register({
          required: {
            value: true,
            message: t('ATTR_INPUT_REQUIRED'),
          },
        })}
      />
    </form>
  );
};

export default AttributeForm;
